"use client";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_COUNTRY } from "@utils/default";
import { mailRegex, nameRegex } from "@utils/textUtils";
import { getCookie, setCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { useReCaptcha } from "next-recaptcha-v3";
import { usePathname } from "next/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { InferType, boolean, object, string } from "yup";

import CheckBox from "@/components//ui/Form/CheckBox/CheckBox";
import { submitContactForm } from "@/components/ContactForm/actions";
import Button, { ButtonSize, ButtonTheme } from "@/components/ui/Button/Button";
import Input from "@/components/ui/Form/Input/Input";
import Telephone from "@/components/ui/Form/Telephone/Telephone";
import Textarea from "@/components/ui/Form/TextArea/Textarea";
import { ModalsContext } from "@/context/ModalsContext";
import { UTMParametersContext, getFallbackCampaignParameters, utmParametersMapping } from "@/context/UTMParametersContext";
import { leadFormSubmission, mortageleadFormSubmission } from "@/dataLayer/dataLayer";
import { prefixList } from "@/utils/data/prefixes";

import TermsAndPrivacy from "../Checkout/TermsAndPrivacy";
import Select from "../ui/Form/Select/Select";

const ContactInformationSchema = object()
  .shape({
    countryID: string(),
    fullName: string().matches(nameRegex, "form.error.fullName.invalid").required("form.error.fullName.required"),
    description: string().matches(/^[a-zA-Z0-9 _,\-"'():.?%&+$#@]*$/, "form.error.description.invalid"),
    designation: string().matches(nameRegex, "form.error.designation.invalid").required("form.error.designation.required"),
    areaOfInterest: string().required("form.error.areaofinterest.required"),
    phone: string()
      .matches(/^[0-9]+$/, "form.error.phone.invalid")
      .test("is-valid-phone", "form.error.phone.invalid", function (value) {
        const { prefix } = this.parent; // Access prefix (country code) from the parent form
        let countryCode = prefix ? prefix.split("_").find((i: string) => i[0] == "+") : "+971";
        const fullPhoneNumber = countryCode + (value || ""); // Combine the prefix and phone number

        return isValidPhoneNumber(fullPhoneNumber); // Validate with the full phone number
      })
      .required("form.error.phone.required"),
    prefix: string().required("form.error.prefix.required"),
    email: string().matches(mailRegex, "form.error.email.invalid").required("form.error.email.required"),
    // newsletterSubscription: boolean(),
    privacy: boolean().required("form.error.privacy.required"),
    utmData: object({ campaignId: string().required() }),
    token: string().required(),
  })
  .required();

export type ContactInformationSchemaType = InferType<typeof ContactInformationSchema>;

type Props = {};

export default function ContactForm({}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const [serverError, setServerError] = useState<string | null>(null);
  const [formSent, setFormSent] = useState<boolean>(false);
  const { additionalInfo, settings } = useContext(ModalsContext);
  const { data: session } = useSession();
  const { utmParameters } = useContext(UTMParametersContext);
  const currentCountry = DEFAULT_COUNTRY ?? locale.slice(3).toUpperCase();
  const defaultPrefix = prefixList.find((prefix) => prefix.code === currentCountry);
  const hcaptchaRef = useRef<any>(null);
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const initialState = useMemo(() => {
    const parameters: { [key: string]: string } = {
      ...getFallbackCampaignParameters(utmParameters?.utm_medium),
      ...utmParameters,
      lastMileConversion: `${pathname?.replaceAll("/", "") === locale ? "Home page" : document.title} Lead Form`,
    };

    const initialStateDefinition = {
      utmData: Object.fromEntries(Object.entries(utmParametersMapping).map(([k, v]) => [v, parameters[k] ?? ""])),
      brochureData: settings?.brochureData,
      additionalInfo,
    } as LeadPayload;

    return initialStateDefinition;
  }, [additionalInfo, locale, pathname, settings?.brochureData, utmParameters]);

  const { control, handleSubmit, formState, setValue, reset, watch } = useForm<ContactInformationSchemaType>({
    resolver: yupResolver(ContactInformationSchema),
    defaultValues: {
      fullName: "",
      phone: "",
      prefix: defaultPrefix?.dial_code + "_" + defaultPrefix?.name,
      countryID: defaultPrefix?.code ?? "AE",
      email: "",
      description: "",
      designation: "",
      areaOfInterest: "",
      ...initialState,
    },
    mode: "onBlur",
  });

  // const { executeRecaptcha, loaded } = useReCaptcha();
  const currentPhoneprefix = watch("prefix");
  const currentCountryID = watch("countryID");
  const privacy = watch("privacy");

  const handleTouchStart = () => {
    if (hcaptchaRef.current) {
      hcaptchaRef.current.execute(); // This will show a new challenge
    }
  };

  // useEffect(() => {
  //   const getToken = async () => {
  //     const token = await executeRecaptcha("contact_form");
  //     setValue("token", token);
  //   };
  //   if (loaded) {
  //     getToken();
  //   }
  // }, [loaded]);

  useEffect(() => {
    console.log("hCaptcha token received:", hcaptchaToken);
    if (hcaptchaToken) {
      console.log("hCaptcha token received:", hcaptchaToken);
      setValue("token", hcaptchaToken, { shouldValidate: true });
    }
  }, [hcaptchaToken, setValue]);

  const action: () => void = handleSubmit(async (data) => {
    setServerError(null);
    const response = await submitContactForm(data);
    if (pathname?.includes("mortgage-calculator")) {
      mortageleadFormSubmission({
        step_number: 2,
        step_name: "form fill started",
        form_name: "RegisterYourInterest",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 3,
        step_name: "form fill finished",
        form_name: "RegisterYourInterest",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 4,
        step_name: "form success",
        form_name: "RegisterYourInterest",
        cta_name: "Request a callback",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
      mortageleadFormSubmission({
        step_number: 5,
        step_name: "sf lead success",
        form_name: "RegisterYourInterest",
        cta_name: "Request a callback",
        user_logged_in: !!session,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
    } else {
      leadFormSubmission({
        step_number: 3,
        step_name: "form fill finished",
        form_name: "bookVisit",
        user_logged_in: !!session,
        // first_name: data.firstName, // 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
        // last_name: data.lastName,
        // email: data.email,
        // phone: data.phone,
        phone_country_code: currentPhoneprefix?.split("_")?.[0],
      });
    }

    if (response) {
      console.info(response);
      if ("error" in response) {
        // const token = await executeRecaptcha("contact_form");
        setValue("token", hcaptchaToken || "");
        setServerError(response.error || t("label.error.generic"));
      } else {
        setFormSent(true);
        reset();
      }
    }
  });

  useEffect(() => {
    if (pathname?.includes("mortgage-calculator")) {
      if (Object.keys(formState.errors).length > 0) {
        Object.entries(formState.errors).forEach(([fieldName, error]) => {
          mortageleadFormSubmission({
            step_number: 1,
            step_name: "form error",
            error_text: error?.message || "",
            error_field: fieldName || "",
            user_logged_in: !!session,
            phone_country_code: currentPhoneprefix?.split("_")?.[0],
            form_name: "RegisterYourInterest",
          });
        });
      }
    }
  }, [formState.errors]);
  console.log("form errors", formState.errors);
  return (
    <form noValidate action={action}>
      <Input control={control} type="text" hidden name="countryID" />
      <div className="grid grid-cols-2 gap-xxs">
        <div className="col-span-1 pb-xxs">
          <Input control={control} name="fullName" label={`${t("form.label.fullName")}`} required reverse />
        </div>
        <div className="col-span-1 pb-xxs">
          <Input control={control} name="email" label={`${t("form.label.businessEmail")}`} required reverse />
        </div>
        <div className="col-span-1 pb-xxs">
          <Input control={control} name="designation" label={`${t("form.label.designation")}`} required reverse />
        </div>
        <div className="col-span-1 pb-xxs">
          <Select
            control={control}
            required
            reverse
            name="areaOfInterest"
            label={t("form.label.areaOfInterest")}
            selectClassName="h-[36px]"
            options={[
              { label: t("form.label.selectOne"), value: "" },
              { label: t("form.label.dataCenterSolutions"), value: "Data Center Solutions" },
              { label: t("form.label.careerHumanResources"), value: "Career and Human Resources" },
              { label: t("form.label.investorRelations"), value: "Investor Relations" },
              { label: t("form.label.mediaRelations"), value: "Media Relations" },
              { label: t("form.label.other"), value: "Other" },
            ]}
          />
        </div>
        <div className="col-span-2 pb-xxs">
          <Telephone
            phoneName="phone"
            prefixName="prefix"
            // onPrefixChange={prefixHandler}
            control={control}
            reverse
            onCountryChange={(country: string) => setValue("countryID", country)}
            controlledCountryCode={currentCountryID}
            showCountryCodeLeadPrefixes={true}
          />
        </div>
        <div className="col-span-2 pb-xxs">
          <Textarea control={control} name="description" label={`${t("form.label.description")}`} reverse />
        </div>
        {/* <div className="col-span-2 pb-xxs">
          <CheckBox control={control} className="invert" label={`${t("form.label.news")}`} name="newsletterSubscription" />
        </div> */}
        <div className="pb-xxs" onTouchStart={handleTouchStart}>
          <HCaptcha
            ref={hcaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY!}
            onVerify={setHcaptchaToken}
            onExpire={() => setValue("token", "")}
          />
          {!!formState.errors.token && (
            <span className="block text-xs leading-tight font-secondary text-[#B42318] pt-xs px-xxs">
              {t(formState.errors.token.message as string)}
            </span>
          )}
        </div>
        <div className="col-span-2 pb-xxs flex ">
          <CheckBox control={control} className="invert" name="privacy" required />
          <span className="text-white">
            <TermsAndPrivacy translationKey="form.label.termsandpolicy" genericLeadForm={true} />
          </span>
        </div>
      </div>

      {formSent && (
        <div
          className=" bg-status-success/30 text-white rounded w-full px-s py-s text-center static my-m"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {t("label.form.sent")}
        </div>
      )}
      <Button
        type="submit"
        size={ButtonSize.MEDIUM}
        theme={ButtonTheme.WHITE}
        customClass="border-none w-full mt-s"
        isDisabled={!formState.isValid || !privacy}
      >
        {t("form.label.requestcallback")}
      </Button>
    </form>
  );
}
